(function () {
  // Configuration
  const PROXY_URL = 'https://vehicle-taxonomy-production.up.railway.app/vehicle-taxonomy';
  const FALLBACK_URLS = [...(window.MaxtonVehicleFallbackUrls ?? []), window.MaxtonVehicleFallbackUrl].filter(
    (url, index, urls) => url && urls.indexOf(url) === index
  );
  const REQUEST_TIMEOUT_MS = 8000;
  const CACHE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
  const SHORT_RETRY_MS = 60 * 1000;
  const CACHE_KEY_PREFIX = 'maxton:vehicle-taxonomy:v2';
  const UPDATE_EVENT = 'maxton:vehicle-data-updated';
  const ROOT_HIDE_FIELDS = ['hide', 'hide_in_json', 'hideInJson'];

  const LOCALE = String(window.Shopify?.locale ?? document.documentElement.lang ?? 'pl').toLowerCase();
  const LOCALE_ROOT = (window.Shopify?.routes?.root ?? '/').replace(/\/$/, '');
  const CACHE_KEY = `${CACHE_KEY_PREFIX}:${LOCALE}`;

  // Dataset validation & sanitizing
  const isValidDataset = (value) => {
    return Boolean(value && Array.isArray(value.nodes) && Number.isFinite(Number(value.rootId)));
  };

  const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object ?? {}, key);

  const isTruthyFlag = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes';
    }

    return false;
  };

  const buildChildrenByParent = (nodes) => {
    const childrenByParent = new Map();

    nodes.forEach((node) => {
      const parentId = Number(node?.parentId);
      if (!Number.isFinite(parentId)) return;

      if (!childrenByParent.has(parentId)) {
        childrenByParent.set(parentId, []);
      }

      childrenByParent.get(parentId).push(node);
    });

    return childrenByParent;
  };

  const collectAllowedDescendants = (rootNodes, childrenByParent) => {
    const allowedNodeIds = new Set();
    const stack = [...rootNodes];

    while (stack.length > 0) {
      const node = stack.pop();
      const nodeId = Number(node?.id);
      if (!Number.isFinite(nodeId) || allowedNodeIds.has(nodeId)) continue;

      allowedNodeIds.add(nodeId);

      const children = childrenByParent.get(nodeId) ?? [];
      children.forEach((child) => stack.push(child));
    }

    return allowedNodeIds;
  };

  const getRootHideField = (rootNodes) => {
    return ROOT_HIDE_FIELDS.find((field) => rootNodes.some((node) => hasOwn(node, field))) ?? null;
  };

  const sanitizeRootBrands = (dataset) => {
    const rootId = Number(dataset?.rootId);
    const nodes = Array.isArray(dataset?.nodes) ? dataset.nodes : [];
    if (!Number.isFinite(rootId) || nodes.length === 0) {
      return dataset;
    }

    const childrenByParent = buildChildrenByParent(nodes);
    const rootNodes = childrenByParent.get(rootId) ?? [];
    if (rootNodes.length === 0) {
      return dataset;
    }

    const hideField = getRootHideField(rootNodes);
    if (!hideField) {
      return dataset;
    }

    const visibleRootNodes = rootNodes.filter((node) => !isTruthyFlag(node?.[hideField]));

    if (visibleRootNodes.length === 0) {
      console.warn(
        `[vehicle-taxonomy-loader] Root visibility field "${hideField}" hid every root node. Using unsanitized dataset.`
      );
      return dataset;
    }

    const allowedNodeIds = collectAllowedDescendants(visibleRootNodes, childrenByParent);

    return {
      ...dataset,
      nodes: nodes.filter((node) => allowedNodeIds.has(Number(node?.id)))
    };
  };

  // Local cache
  const readCachedTaxonomy = () => {
    try {
      const entry = JSON.parse(window.localStorage.getItem(CACHE_KEY));
      return isValidDataset(entry?.payload) ? entry : null;
    } catch {
      return null;
    }
  };

  const writeCachedTaxonomy = (entry) => {
    try {
      window.localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
      return true;
    } catch {
      return false;
    }
  };

  const isCacheCheckDue = ({ checkedAt = 0 }) => Date.now() - checkedAt >= CACHE_CHECK_INTERVAL_MS;

  const isLocaleMismatch = (payload) => Boolean(payload?.locale) && payload.locale.toLowerCase() !== LOCALE;

  const buildShortRetryCheckedAt = () => Date.now() - CACHE_CHECK_INTERVAL_MS + SHORT_RETRY_MS;

  const resolveCheckedAt = (payload, source) =>
    source === 'fallback' || isLocaleMismatch(payload) ? buildShortRetryCheckedAt() : Date.now();

  const markCacheAsChecked = (entry) =>
    writeCachedTaxonomy({ ...entry, checkedAt: resolveCheckedAt(entry.payload, entry.source) });

  // Network
  const fetchWithTimeout = async (url) => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      return await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal: controller.signal
      });
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const parseDatasetResponse = async (response) => {
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const payload = await response.json();
    if (!isValidDataset(payload)) {
      throw new Error('Invalid payload shape');
    }

    return payload;
  };

  const buildProxyUrl = (knownVersion) => {
    const url = new URL(PROXY_URL);
    url.searchParams.set('locale', LOCALE);
    if (knownVersion) url.searchParams.set('version', knownVersion);
    return url;
  };

  const fetchTaxonomyFromProxy = async (knownVersion = null) => {
    const response = await fetchWithTimeout(buildProxyUrl(knownVersion));
    return response.status === 204 ? null : parseDatasetResponse(response);
  };

  const fetchTaxonomyFromFallbackFile = async () => {
    if (!FALLBACK_URLS.length) throw new Error('Fallback URL is not configured');

    const errors = [];

    for (const url of FALLBACK_URLS) {
      try {
        return await parseDatasetResponse(await fetchWithTimeout(url));
      } catch (error) {
        errors.push(`${url}: ${error.message}`);
      }
    }

    throw new Error(`Fallback files failed. ${errors.join(' | ')}`);
  };

  // Publishing
  const localizeTaxonomyLinks = (payload) => {
    if (!LOCALE_ROOT) return payload;
    return { ...payload, nodes: payload.nodes.map((node) => ({ ...node, link: `${LOCALE_ROOT}${node.link}` })) };
  };

  const publishTaxonomy = (payload, source) => {
    const data = sanitizeRootBrands(localizeTaxonomyLinks(payload));
    window.MaxtonVehicleData = data;
    window.MaxtonVehicleDataSource = source;
    return data;
  };

  // Proxy synchronization (returns null when the proxy reports no change)
  const syncTaxonomyWithProxy = async (cachedEntry) => {
    try {
      const payload = await fetchTaxonomyFromProxy(cachedEntry?.version);

      if (payload) {
        writeCachedTaxonomy({
          payload,
          version: payload.version ?? null,
          checkedAt: resolveCheckedAt(payload, 'proxy'),
          source: 'proxy'
        });
      } else {
        markCacheAsChecked(cachedEntry);
      }

      return payload;
    } catch (error) {
      if (cachedEntry) writeCachedTaxonomy({ ...cachedEntry, checkedAt: buildShortRetryCheckedAt() });
      throw error;
    }
  };

  const refreshTaxonomyInBackground = (cachedEntry) => {
    syncTaxonomyWithProxy(cachedEntry)
      .then((payload) => {
        if (!payload) return;
        const data = publishTaxonomy(payload, 'proxy');
        window.dispatchEvent(new CustomEvent(UPDATE_EVENT, { detail: data }));
      })
      .catch((error) => console.warn(`[vehicle-taxonomy-loader] Background refresh failed: ${error.message}`));
  };

  const loadTaxonomyFromFallbackFile = async () => {
    const payload = await fetchTaxonomyFromFallbackFile();
    writeCachedTaxonomy({ payload, version: null, checkedAt: buildShortRetryCheckedAt(), source: 'fallback' });
    return publishTaxonomy(payload, 'fallback');
  };

  // Loading order: local cache -> proxy -> fallback file
  const loadVehicleTaxonomy = async () => {
    const cachedEntry = readCachedTaxonomy();
    if (cachedEntry) {
      if (isCacheCheckDue(cachedEntry)) refreshTaxonomyInBackground(cachedEntry);
      return publishTaxonomy(cachedEntry.payload, `cache (${cachedEntry.source})`);
    }

    try {
      return publishTaxonomy(await syncTaxonomyWithProxy(null), 'proxy');
    } catch (proxyError) {
      try {
        return await loadTaxonomyFromFallbackFile();
      } catch (fallbackError) {
        throw new Error(
          `All vehicle taxonomy sources failed. proxy: ${proxyError.message} | fallback: ${fallbackError.message}`
        );
      }
    }
  };

  // Debug helpers
  window.MaxtonVehicleDebug = {
    source: () => window.MaxtonVehicleDataSource,
    readCache: readCachedTaxonomy,
    clearCache: () => window.localStorage.removeItem(CACHE_KEY)
  };

  // Bootstrap
  if (!window.MaxtonVehicleDataPromise) {
    window.MaxtonVehicleDataPromise = loadVehicleTaxonomy();
    window.MaxtonVehicleDataPromise.catch((error) => console.error(`[vehicle-taxonomy-loader] ${error.message}`));
  }
})();